import { useState, useEffect } from "react";
import { supabase, BRAND, MUTED, LINE, SURFACE, TEXT, pageTitle, text, btn, btnSm, linkBtn, errorBanner, savedText, INK, FAINT, WHITE, segWrap, segBtn } from "./constants";
import { JDFormatted, stripRules, HelpButton } from "./shared";
import OnboardingModal from "./OnboardingModal";
import { Hexagon, BookOpen, FileText } from "lucide-react";
import { useIsMobile } from "./lib/responsive";
import { logoHtml, LOGO_FONT_LINK } from "./Logo";
import { CultureProfileBoard, hasCultureProfile } from "./CultureProfile";
import CultureMethodology from "./CultureMethodology";
import { cleanAnswers } from "./lib/culture";

const DocumentsView = ({ t, lang, setView, selectedClientId, companyName, openPositionsList = [], companyLogoUrl = null, isAdmin = false }) => {
  const isMobile = useIsMobile();
  const [cultureDoc, setCultureDoc] = useState("");
  const [cultureAnswers, setCultureAnswers] = useState({});
  const [helpOpen, setHelpOpen] = useState(false); // «?» del título
  const [tabSel, setTabSel] = useState(null); // pestaña elegida; sin elegir, Perfil si lo hay
  const [editingCulture, setEditingCulture] = useState(false);
  const [savingCulture, setSavingCulture] = useState(false);
  const [savedCulture, setSavedCulture] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  useEffect(() => {
    // Sin cliente elegido no se lee nada. Antes caía en el client_id 2
    // (Meetzy, la demo) y se podía sobrescribir su documento.
    setCultureDoc(""); setCultureAnswers({});
    if (!selectedClientId) return;
    supabase.from("culture_docs").select("generated_doc, answers").eq("client_id", selectedClientId).maybeSingle()
      .then(({ data }) => { if (data?.generated_doc) setCultureDoc(data.generated_doc); setCultureAnswers(cleanAnswers(data?.answers)); });
  }, [selectedClientId]);

  const downloadPDF = (title, text, isculture) => {
    const win = window.open("", "_blank");
    const body = stripRules(text).split('\n').map(line => {
      const tr = line.trim();
      if (!tr) return '<div style="height:8px"></div>';
      const hm = tr.match(/^\*\*(.+?)\*\*$/) || tr.match(/^##\s+(.+)$/);
      if (hm) return `<div style="font-size:11px;font-weight:800;color:${BRAND};text-transform:uppercase;letter-spacing:1px;margin-top:20px;margin-bottom:6px;">${hm[1]}</div>`;
      if (tr.startsWith('•') || tr.startsWith('-')) return `<div style="display:flex;gap:8px;margin-bottom:4px;"><div style="width:4px;height:4px;border-radius:50%;background:${BRAND};margin-top:7px;flex-shrink:0;"></div><span style="font-size:12px;color:${INK};line-height:1.7;">${tr.replace(/^[•-]\s*/,'')}</span></div>`;
      return `<div style="font-size:12px;color:${INK};line-height:1.7;margin-bottom:3px;">${tr}</div>`;
    }).join('');
    const clientLogoHtml = companyLogoUrl ? `<img src="${companyLogoUrl}" style="height:32px;object-fit:contain;" />` : (companyName ? `<span style="font-weight:700;color:${INK};">${companyName}</span>` : "");
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><link href="${LOGO_FONT_LINK}" rel="stylesheet"><title>${title}</title><style>@page{margin:2cm}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}</style></head><body><div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:18px;border-bottom:2px solid ${BRAND};margin-bottom:28px;"><div style="font-size:20px;font-weight:800;color:${INK};">${title}</div>${clientLogoHtml}</div>${body}<div style="margin-top:48px;padding-top:16px;border-top:1px solid ${LINE};font-size:10px;color:${FAINT};"><span style="display:inline-flex;align-items:center;gap:6px;">Powered by ${logoHtml(11)}</span></div></body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 400);
  };


  // Tres pestañas (30/10): Perfil (hexágono con una caja por dimensión),
  // Metodología (de dónde sale el perfil) y Documento (el texto, que se lee,
  // edita y descarga aquí mismo, sin ventana flotante). Mismo conmutador que
  // las pestañas de la posición. Sin perfil todavía, se abre en Documento.
  const hasProfile = hasCultureProfile(cultureAnswers);
  const tab = tabSel || (hasProfile ? "profile" : "document");
  const es = lang === "es";
  const docTitle = es ? `Cultura de ${companyName || "la empresa"}` : `${companyName || "Company"} Culture`;
  const mBtn = isMobile ? { height: 40, padding: "0 14px" } : null;
  const ghostSm = { ...btn.ghost, ...btnSm, display: "flex", alignItems: "center", gap: 5, flexShrink: 0, ...mBtn };
  // 03/11: las tres pestañas son la MISMA caja (mismo borde, relleno y alto
  // mínimo) con la misma cabecera: título de tarjeta, una línea gris y, a la
  // derecha, sus acciones. Dentro, cajas de 13/800 con texto a 13.
  const panel = { background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14, padding: isMobile ? "16px" : "24px 28px", minHeight: isMobile ? undefined : 560, boxSizing: "border-box" };
  const panelHead = (title, sub, right) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
      <div style={{ minWidth: 0 }}>
        <div style={text.cardTitle}>{title}</div>
        {sub && <div style={{ ...text.body, color: MUTED, marginTop: 2 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
  const emptyBox = (title, msg) => (
    <div style={panel}>{panelHead(title, msg)}</div>
  );
  const saveDoc = async () => {
    if (!selectedClientId) return;
    setSavingCulture(true);
    const { data, error } = await supabase.from("culture_docs").update({ generated_doc: cultureDoc, updated_at: new Date().toISOString() }).eq("client_id", selectedClientId).select("client_id");
    setSavingCulture(false);
    if (error || !data || data.length === 0) { setSaveErr(es ? "No se pudo guardar. Inténtalo de nuevo." : "Couldn't save. Please try again."); return; }
    setSaveErr(""); setSavedCulture(true); setTimeout(() => setSavedCulture(false), 2000);
    setEditingCulture(false);
  };

  return (
    <div style={{ padding: isMobile ? 16 : 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <h1 style={pageTitle(isMobile, 0)}>
          {es ? "Documento de cultura" : "Culture statement"}
        </h1>
        <HelpButton onClick={() => setHelpOpen(true)} label={es ? "Cómo funciona" : "How it works"} />
      </div>
      {helpOpen && <OnboardingModal help="culture" lang={lang} onClose={() => setHelpOpen(false)} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: MUTED }}>
          {es ? "Se comparte con cada candidato antes de la entrevista." : "Shared with every candidate before the interview."}
        </span>
        <button onClick={() => setView("cultureQuestionnaire")} style={{ ...linkBtn, flexShrink: 0, ...(isMobile ? { minHeight: 40, padding: "0 4px", fontSize: 13 } : null) }}>
          {cultureDoc ? (es ? "Rehacer →" : "Redo →") : (es ? "Crear →" : "Create →")}
        </button>
      </div>

      {(!selectedClientId || saveErr) && (
        <div style={saveErr ? { ...errorBanner, marginBottom: 16 } : { background: SURFACE, border: "1.5px solid " + LINE, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: TEXT, lineHeight: 1.6, marginBottom: 16 }}>
          {saveErr || (es ? "Elige un cliente en «Cliente visible» para ver su cultura." : "Pick a client in “Viewing client” to see its culture.")}
        </div>
      )}

      <div style={{ paddingBottom: 16, marginBottom: 20, borderBottom: `1px solid ${LINE}` }}>
        <div style={isMobile
          ? { ...segWrap, width: "100%", boxSizing: "border-box", alignItems: "center" }
          : { ...segWrap, display: "inline-flex", boxSizing: "border-box", alignItems: "center" }}>
          {[["profile", Hexagon, es ? "Perfil" : "Profile"],
            ["methodology", BookOpen, es ? "Metodología" : "Methodology"],
            ["document", FileText, es ? "Documento" : "Document"]].map(([v, Ico, label]) => {
            const active = tab === v;
            return (
              <button key={v} onClick={() => setTabSel(v)}
                style={{ ...segBtn(active), padding: "0 18px", height: 34, display: "inline-flex", alignItems: "center", gap: 8, transition: "all 0.12s",
                  ...(isMobile ? { height: 40, padding: "0 10px", flex: "1 1 0", justifyContent: "center", gap: 6 } : null) }}>
                <Ico size={16} strokeWidth={1.75} color={active ? BRAND : MUTED} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "profile" && (hasProfile ? (
        <div style={panel}>
          {panelHead(es ? "Perfil de cultura" : "Culture profile",
            isMobile ? (es ? "6 dimensiones, de 1 a 5. Toca una para ver qué mide." : "6 dimensions, 1 to 5. Tap one to see what it measures.")
              : (es ? "6 dimensiones, de 1 a 5. Pasa el ratón por una para ver qué mide." : "6 dimensions, 1 to 5. Hover over one to see what it measures."))}
          <CultureProfileBoard answers={cultureAnswers} lang={lang} showMap={isAdmin} />
        </div>
      ) : emptyBox(es ? "Perfil de cultura" : "Culture profile", es ? "Aún no hay perfil de cultura. Sale al contestar el cuestionario (14 preguntas, 10-12 minutos)." : "No culture profile yet. It comes from the questionnaire (14 questions, 10-12 minutes)."))}

      {tab === "methodology" && (
        <div style={panel}>
          {panelHead(es ? "Metodología" : "Methodology",
            es ? "6 dimensiones a partir de 14 preguntas, cada una con base académica y calculada siempre igual." : "6 dimensions from 14 questions, each grounded in academic research and always calculated the same way.")}
          <CultureMethodology lang={lang} />
        </div>
      )}

      {tab === "document" && (cultureDoc ? (
        <div style={panel}>
          {panelHead(docTitle, es ? "El texto que recibe cada candidato antes de la entrevista." : "The text every candidate receives before the interview.", (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {savedCulture && <span style={savedText}>{es ? "✓ Guardado" : "✓ Saved"}</span>}
              <button onClick={() => downloadPDF(docTitle, cultureDoc, true)} style={ghostSm}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>PDF
              </button>
              <button onClick={() => (editingCulture ? saveDoc() : setEditingCulture(true))}
                style={{ ...(editingCulture ? btn.primary : btn.ghost), ...btnSm, ...mBtn }}>
                {editingCulture ? (savingCulture ? "..." : (es ? "Guardar" : "Save")) : (es ? "Editar" : "Edit")}
              </button>
            </div>
          ))}
          <div style={{ maxWidth: 760 }}>
          {editingCulture ? (
            <textarea value={cultureDoc} onChange={e => setCultureDoc(e.target.value)} rows={22}
              style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", border: `1.5px solid ${BRAND}`, borderRadius: 10, fontSize: isMobile ? 16 : 13, lineHeight: 1.75, resize: "vertical", outline: "none", fontFamily: "inherit", color: INK }} />
          ) : (
            <JDFormatted text={cultureDoc} />
          )}
          </div>
        </div>
      ) : emptyBox(docTitle, es ? "No hay documento de cultura todavía. Créalo desde el cuestionario." : "No culture document yet. Create one from the questionnaire."))}
    </div>
  );
};


export default DocumentsView;
